/**
 * Skins — the CLIENT half of their community adapter. Server half (the real safety check, which decodes and
 * re-encodes every pixel): `./skin.server.ts`. State format and layout: `../skinLayout.ts`.
 */
import type { LibraryItem } from '@/app/saved/lib/library';
import SkinDesignPreview from '@/app/designs/components/SkinDesignPreview';
import { parseSkinState, skinDataUrl, skinShapeProblem } from '../skinLayout';
import type { PublicDesign } from '../shared';
import type { ClientDesignAdapter } from './contract';

/** The canonical PNG, served same-origin for the editor and downloads (`app/api/designs/[id]/skin/route.ts`). */
export function skinFileUrl(designId: string): string {
  return `/api/designs/${designId}/skin`;
}

function parts(design: PublicDesign) {
  const parsed = parseSkinState(design.state);
  return { model: parsed?.model ?? 'classic', dataUrl: parsed ? skinDataUrl(parsed.base64) : '' };
}

/** `Thumbnail` draws a saved skin from `metadata.textureBase64` + `modelType` — the same fields `saveSkin` writes. */
function toLibraryItem(design: PublicDesign): LibraryItem {
  const { model, dataUrl } = parts(design);
  return {
    uid: `design-${design.id}`,
    source: 'element',
    id: 0,
    type: 'skin',
    name: design.title,
    version: null,
    createdAt: design.createdAt,
    folder: null,
    tags: [],
    pinned: false,
    openPath: '',
    buttonLabel: '',
    summary: '',
    metadata: { textureBase64: dataUrl, modelType: model },
  };
}

export const skinClient: ClientDesignAdapter = {
  toLibraryItem,
  // `skinUrl` makes the link work on its own (new tab, shared link) once the editor loads a same-origin URL
  // without its image proxy; today the texture arrives through `prepareOpen` (same tab), which the editor
  // prefers when both are present.
  openHref: design => `/tools/skin-editor?skinUrl=${encodeURIComponent(skinFileUrl(design.id))}&model=${parts(design).model}`,
  prepareOpen: design => {
    const { dataUrl } = parts(design);
    if (!dataUrl) return;
    try { sessionStorage.setItem('skinEditor_loadBase64', dataUrl); } catch { /* storage blocked: the editor falls back to skinUrl */ }
  },
  saveCopy: (saved, design) => {
    const { model, dataUrl } = parts(design);
    if (!dataUrl) return Promise.resolve({ error: 'That skin could not be read.' });
    return saved.saveSkin({ name: design.title, modelType: model, textureBase64: dataUrl });
  },
  // Structural only (size, PNG header): the pixel checks need zlib and run on the server.
  publishProblem: skinShapeProblem,
  Preview: SkinDesignPreview,
  download: design => ({
    href: skinFileUrl(design.id),
    // A filename from the title, reduced to characters every file system accepts.
    filename: `${design.title.replace(/[^\p{L}\p{N} _-]+/gu, '').trim().slice(0, 60) || 'skin'}.png`,
    label: 'Download skin',
  }),
};
